import MobileMenu, { MobileMenuCSS } from "./MobileMenu";
import React, { useState, useEffect } from "react";
import { supabase } from "./supabaseClient";
import { useToast, ToastContainer, TableRowSkeleton, ConfirmDialog } from "./toast";

/* ─────────────────────────────────────────────────────────
   TRACKED LEGISLATION
   Shows the user's personal bill watchlist.
   On load: checks getMasterListRaw hashes against stored
   change_hash values for each tracked bill.
   Only calls getBill for bills whose hash has changed.
   Attribution: Data provided by LegiScan (legiscan.com)
───────────────────────────────────────────────────────── */

const PROXY = process.env.REACT_APP_PROXY_URL || "https://legisim-server.onrender.com";
const CACHE_KEY_BILLS = "wc_db_bills_v2";

// ─── SUPABASE WATCHLIST HELPERS ───────────────────────────
async function getWatchlist(userId) {
  if (!userId) return [];
  try {
    const { data, error } = await supabase
      .from("tracked_bills")
      .select("*")
      .eq("user_id", userId)
      .order("sort_order", { ascending: true, nullsFirst: false }).order("tracked_at", { ascending: false });
    if (error) throw error;
    return data || [];
  } catch { return []; }
}

async function removeFromWatchlist(userId, bill_id) {
  if (!userId) return;
  try {
    await supabase
      .from("tracked_bills")
      .delete()
      .eq("user_id", userId)
      .eq("bill_id", bill_id);
  } catch {}
}

async function saveNotes(userId, bill_id, notes) {
  if (!userId) return;
  try {
    await supabase
      .from("tracked_bills")
      .update({ notes })
      .eq("user_id", userId)
      .eq("bill_id", bill_id);
  } catch {}
}

async function saveStance(userId, bill_id, stance) {
  if (!userId) return;
  try {
    await supabase
      .from("tracked_bills")
      .update({ stance })
      .eq("user_id", userId)
      .eq("bill_id", bill_id);
  } catch {}
}

async function saveNickname(userId, bill_id, nickname) {
  if (!userId) return;
  try {
    await supabase
      .from("tracked_bills")
      .update({ nickname })
      .eq("user_id", userId)
      .eq("bill_id", bill_id);
  } catch {}
}

async function updateWatchlistItem(userId, updated) {
  if (!userId) return;
  try {
    await supabase
      .from("tracked_bills")
      .update({
        status: updated.status,
        last_action: updated.last_action,
        last_action_date: updated.last_action_date,
        change_hash: updated.change_hash,
        last_seen_at: new Date().toISOString(),
      })
      .eq("user_id", userId)
      .eq("bill_id", updated.bill_id);
  } catch {}
}

// ─── BILL DETAIL CACHE (localStorage) ────────────────────
function getBillCache(bill_id) {
  try {
    const all = JSON.parse(localStorage.getItem(CACHE_KEY_BILLS) || "{}");
    return all[bill_id] || null;
  } catch { return null; }
}
function setBillCache(bill_id, bill, change_hash) {
  try {
    const all = JSON.parse(localStorage.getItem(CACHE_KEY_BILLS) || "{}");
    all[bill_id] = { bill, change_hash, cached_at: Date.now() };
    localStorage.setItem(CACHE_KEY_BILLS, JSON.stringify(all));
  } catch {}
}

// ─── STATUS HELPERS ───────────────────────────────────────
const STATUS_MAP = { 1:"Introduced",2:"Engrossed",3:"Enrolled",4:"Passed",5:"Vetoed",6:"Failed",7:"Override",8:"Chaptered",9:"Refer",10:"Report Pass",11:"Report Fail",12:"Draft" };
function statusLabel(code) { return STATUS_MAP[code] || "Introduced"; }

const STATUS_COLORS = {
  "Passed":     { bg:"rgba(59,163,96,0.1)",  border:"rgba(59,163,96,0.25)",  text:"#3ba360" },
  "Enrolled":   { bg:"rgba(59,163,96,0.1)",  border:"rgba(59,163,96,0.25)",  text:"#3ba360" },
  "Chaptered":  { bg:"rgba(59,163,96,0.1)",  border:"rgba(59,163,96,0.25)",  text:"#3ba360" },
  "Engrossed":  { bg:"rgba(201,168,76,0.1)", border:"rgba(201,168,76,0.25)", text:"var(--gold)" },
  "Report Pass":{ bg:"rgba(201,168,76,0.1)", border:"rgba(201,168,76,0.25)", text:"var(--gold)" },
  "Introduced": { bg:"rgba(100,100,200,0.1)",border:"rgba(100,100,200,0.25)",text:"#8888dd" },
  "Draft":      { bg:"rgba(100,100,200,0.1)",border:"rgba(100,100,200,0.25)",text:"#8888dd" },
  "Failed":     { bg:"rgba(212,59,59,0.1)",  border:"rgba(212,59,59,0.25)",  text:"var(--rep)" },
  "Vetoed":     { bg:"rgba(212,59,59,0.1)",  border:"rgba(212,59,59,0.25)",  text:"var(--rep)" },
  "Report Fail":{ bg:"rgba(212,59,59,0.1)",  border:"rgba(212,59,59,0.25)",  text:"var(--rep)" },
};
function statusStyle(s) { return STATUS_COLORS[s] || STATUS_COLORS["Introduced"]; }

/* ─── STYLES ─────────────────────────────────────────────── */

function displayStatus(s) {
  const MAP = {
    "Introduced":"Introduced","Engrossed":"In Progress","Enrolled":"PA Signed",
    "Chaptered":"PA Signed","Passed":"Passed","Failed":"Failed","Vetoed":"Vetoed",
    "Report Pass":"In Committee","Report Fail":"In Committee","Refer":"In Committee",
    "Override":"Passed","Draft":"Draft",
  };
  return MAP[s] || s || "Introduced";
}
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;0,800;0,900;1,700&family=IBM+Plex+Sans:wght@300;400;500&family=IBM+Plex+Mono:wght@400;500&display=swap');
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
/* Theme variables defined globally in public/theme.css */
body{background:var(--bg);color:var(--text);font-family:var(--font-body);-webkit-font-smoothing:antialiased;}
.tl-app{display:flex;flex-direction:column;height:100vh;overflow:hidden;background:var(--bg);}
.legisim-masthead{flex-shrink:0;background:var(--surface);border-bottom:2px solid rgba(201,168,76,0.22);padding:0 1.25rem;display:flex;align-items:center;justify-content:space-between;height:58px;position:relative;z-index:20;}
.legisim-masthead::before{content:'';position:absolute;top:0;left:0;right:0;height:2px;background:linear-gradient(90deg,transparent 0%,var(--gold) 30%,#e8c96a 50%,var(--gold) 70%,transparent 100%);}
.legisim-masthead::after{content:'';position:absolute;inset:0;background-image:linear-gradient(rgba(201,168,76,0.025) 1px,transparent 1px),linear-gradient(90deg,rgba(201,168,76,0.025) 1px,transparent 1px);background-size:44px 44px;pointer-events:none;}
.masthead-left{display:flex;align-items:center;gap:1rem;z-index:1;}
.masthead-right{display:flex;align-items:center;gap:0.6rem;z-index:1;}
.mast-back{background:transparent;border:2px solid var(--gold-dim);border-radius:8px;padding:0.45rem 1.1rem;color:var(--gold);font-family:var(--font-mono);font-size:0.72rem;font-weight:600;cursor:pointer;letter-spacing:0.07em;transition:all 0.18s;white-space:nowrap;}
.mast-back:hover{border-color:var(--gold);background:var(--gold-glow);}
.mast-btn-sm{background:transparent;border:1px solid var(--border);border-radius:8px;padding:0.4rem 1rem;color:var(--muted);font-family:var(--font-mono);font-size:0.7rem;cursor:pointer;letter-spacing:0.06em;transition:all 0.18s;white-space:nowrap;}
.mast-btn-sm:hover{border-color:var(--gold-dim);color:var(--gold);}
.mast-btn-sm.signout:hover{border-color:rgba(192,57,43,0.5);color:var(--red);}
.masthead-wordmark{font-family:var(--font-display);font-size:1.35rem;font-weight:800;letter-spacing:-0.02em;color:var(--text);}
.masthead-wordmark em{font-style:italic;color:var(--gold);}
.masthead-wordmark sup{font-family:var(--font-mono);font-size:0.42rem;color:var(--muted);vertical-align:super;}
.masthead-divider{width:1px;height:20px;background:var(--border);}
.masthead-tagline{font-family:var(--font-mono);font-size:0.58rem;color:var(--muted);letter-spacing:0.12em;text-transform:uppercase;}
.masthead-badge{display:inline-flex;align-items:center;gap:0.3rem;background:rgba(201,168,76,0.08);border:1px solid rgba(201,168,76,0.2);border-radius:100px;padding:0.18rem 0.6rem;font-family:var(--font-mono);font-size:0.54rem;color:var(--gold);letter-spacing:0.1em;text-transform:uppercase;}
.masthead-badge-dot{width:5px;height:5px;border-radius:50%;background:var(--gold);animation:mbpulse 2.5s infinite;}
@keyframes mbpulse{0%,100%{opacity:1}50%{opacity:0.4}}
.masthead-nav{display:flex;align-items:center;gap:0.3rem;z-index:1;flex-shrink:1;overflow:hidden;}
.mast-btn{background:transparent;border:1px solid var(--border);border-radius:6px;padding:0.36rem 0.7rem;color:var(--muted);font-family:var(--font-mono);font-size:0.64rem;cursor:pointer;letter-spacing:0.07em;transition:all 0.18s;white-space:nowrap;}
.mast-btn:hover{border-color:var(--gold-dim);color:var(--gold);background:rgba(201,168,76,0.05);}
.mast-btn.gold{border-color:var(--gold-dim);color:var(--gold);}
.mast-btn.gold:hover{background:rgba(201,168,76,0.1);}
.mast-btn.active{border-color:var(--gold);color:var(--gold);background:rgba(201,168,76,0.1);}
.mast-sep{width:1px;height:18px;background:var(--border);margin:0 0.2rem;}
.tl-body{flex:1;overflow-y:auto;scrollbar-width:thin;scrollbar-color:var(--border) transparent;}
.tl-header{padding:2rem 2.5rem 1.5rem;border-bottom:1px solid var(--border);display:flex;align-items:flex-end;justify-content:space-between;flex-wrap:wrap;gap:1rem;background:var(--surface);}
.tl-title{font-family:var(--font-display);font-size:2.4rem;font-weight:900;color:var(--text);letter-spacing:-0.02em;margin-bottom:0.3rem;}
.tl-subtitle{font-family:var(--font-mono);font-size:0.62rem;color:var(--muted);letter-spacing:0.08em;}
.tl-header-right{display:flex;align-items:center;gap:0.75rem;flex-wrap:wrap;}
.tl-refresh-btn{background:var(--gold-glow);border:1px solid var(--gold-dim);border-radius:6px;padding:0.3rem 0.75rem;color:var(--gold);font-family:var(--font-mono);font-size:0.62rem;cursor:pointer;letter-spacing:0.06em;transition:all 0.18s;}
.tl-refresh-btn:hover{background:rgba(201,168,76,0.2);}
.tl-add-btn{background:transparent;border:1px solid var(--gold-dim);border-radius:6px;padding:0.3rem 0.75rem;color:var(--gold);font-family:var(--font-mono);font-size:0.62rem;cursor:pointer;letter-spacing:0.06em;transition:all 0.18s;}
.tl-add-btn:hover{background:var(--gold-glow);}
.tl-refreshing{display:inline-flex;align-items:center;gap:0.4rem;font-family:var(--font-mono);font-size:0.62rem;color:var(--muted);}
.tl-spin{width:12px;height:12px;border:1.5px solid var(--border);border-top-color:var(--gold);border-radius:50%;animation:spin 0.8s linear infinite;flex-shrink:0;}
@keyframes spin{to{transform:rotate(360deg)}}
.tl-empty{display:flex;flex-direction:column;align-items:center;justify-content:center;gap:1rem;padding:5rem 2rem;text-align:center;}
.tl-empty-icon{font-size:3rem;opacity:0.15;}
.tl-empty-title{font-family:var(--font-display);font-size:1.1rem;font-weight:700;color:var(--text);}
.tl-empty-text{font-family:var(--font-mono);font-size:0.68rem;color:var(--muted);line-height:1.7;max-width:360px;}
.tl-page-title{font-family:var(--font-display);font-size:2.8rem;font-weight:900;color:var(--text);letter-spacing:-0.03em;margin-bottom:0.25rem;}
.tl-page-title em{font-style:italic;color:var(--gold);}
.tl-search-input{background:var(--surface2);border:1px solid var(--border);border-radius:8px;padding:0.5rem 1rem;font-family:var(--font-mono);font-size:0.75rem;color:var(--text);outline:none;transition:border-color 0.18s;width:260px;}
.tl-search-input:focus{border-color:rgba(201,168,76,0.4);}
.tl-search-input::placeholder{color:var(--muted);}
.tl-table-wrap{padding:1rem 2rem 2rem;}
.tl-table{width:100%;border-collapse:collapse;border:1px solid var(--border);border-radius:10px;overflow:hidden;}
.tl-table-head{background:var(--charcoal);}
.tl-th{font-family:var(--font-mono);font-size:0.62rem;color:var(--muted);letter-spacing:0.1em;text-transform:uppercase;padding:0.75rem 1rem;text-align:left;border-bottom:1px solid var(--border);white-space:nowrap;}
.tl-th:last-child{text-align:right;}
.tl-row{border-bottom:1px solid var(--border);transition:background 0.15s;}
.tl-row:last-child{border-bottom:none;}
.tl-row:hover{background:var(--surface2);}
.tl-row.updated{border-left:3px solid var(--gold);}
.tl-td{padding:0.75rem 1rem;vertical-align:middle;}
.tl-bill-number{font-family:var(--font-mono);font-size:0.95rem;font-weight:600;color:var(--gold);letter-spacing:0.08em;margin-bottom:0.2rem;}
.tl-bill-title{font-size:0.88rem;font-weight:500;color:var(--text);line-height:1.4;}
.tl-nickname-wrap{display:flex;align-items:center;gap:0.4rem;margin-top:0.15rem;}
.tl-nickname-input{background:transparent;border:none;border-bottom:1px dashed var(--border);color:var(--gold);font-family:var(--font-mono);font-size:0.82rem;font-weight:600;padding:0.1rem 0.25rem;outline:none;width:160px;transition:all 0.18s;font-style:italic;letter-spacing:0.03em;}
.tl-nickname-input:focus{border-bottom-color:var(--gold);color:var(--gold);background:var(--surface2);border-radius:4px 4px 0 0;}
.tl-nickname-input::placeholder{color:var(--muted);font-style:italic;font-weight:400;opacity:0.7;}
.tl-nickname-label{font-family:var(--font-mono);font-size:0.6rem;color:var(--border);flex-shrink:0;}
.tl-bill-chamber{font-family:var(--font-mono);font-size:0.62rem;color:var(--muted);margin-top:0.2rem;}
.tl-status-badge{display:inline-block;font-family:var(--font-mono);font-size:0.65rem;padding:0.22rem 0.6rem;border-radius:5px;font-weight:500;white-space:nowrap;}
.tl-updated-badge{display:inline-block;font-family:var(--font-mono);font-size:0.58rem;color:var(--gold);background:var(--gold-glow);border:1px solid var(--gold-dim);border-radius:4px;padding:0.15rem 0.4rem;margin-left:0.4rem;}
.tl-action-text{font-size:0.82rem;color:var(--text);line-height:1.4;max-width:280px;}
.tl-date-text{font-family:var(--font-mono);font-size:0.68rem;color:var(--muted);margin-top:0.2rem;}
.tl-row-actions{display:flex;gap:0.5rem;justify-content:flex-end;white-space:nowrap;}
.tl-remove-btn{background:transparent;border:1px solid var(--border);border-radius:5px;padding:0.28rem 0.65rem;color:var(--muted);font-family:var(--font-mono);font-size:0.62rem;cursor:pointer;transition:all 0.18s;}
.tl-remove-btn:hover{border-color:var(--rep);color:var(--rep);background:rgba(212,59,59,0.05);}
.tl-view-btn{background:transparent;border:1px solid var(--gold-dim);border-radius:5px;padding:0.28rem 0.65rem;color:var(--gold);font-family:var(--font-mono);font-size:0.62rem;cursor:pointer;transition:all 0.18s;}
.tl-view-btn:hover{background:var(--gold-glow);}
.tl-stance-btn{border-radius:5px;padding:0.28rem 0.65rem;font-family:var(--font-mono);font-size:0.62rem;cursor:pointer;transition:all 0.18s;border:1px solid var(--border);background:transparent;color:var(--muted);}
.tl-stance-btn.support{background:#1a7a3a;border-color:#1a7a3a;color:#ffffff !important;}
.tl-stance-btn.neutral{background:#4a4a5a;border-color:#4a4a5a;color:#cccccc !important;}
.tl-stance-btn.oppose{background:#8b1a1a;border-color:#8b1a1a;color:#ffffff !important;}
.tl-stance-btn:hover{opacity:0.85;}
.tl-stance-btn option{background:var(--card) !important;color:var(--text) !important;}
[data-theme="light"] .tl-stance-btn.support{background:#2e9e52;border-color:#2e9e52;}
[data-theme="light"] .tl-stance-btn.neutral{background:#a0a0b0;border-color:#a0a0b0;color:#fff !important;}
[data-theme="light"] .tl-stance-btn.oppose{background:#c0392b;border-color:#c0392b;}
[data-theme="light"] .tl-stance-btn option{background:#ffffff !important;color:#09090e !important;}
.tl-notes-btn{background:#3d6cc4;border:1px solid #3d6cc4;border-radius:5px;padding:0.28rem 0.65rem;color:#ffffff !important;font-family:var(--font-mono);font-size:0.62rem;cursor:pointer;transition:all 0.18s;}
.tl-notes-btn:hover{background:#2952b3;border-color:#2952b3;}
.tl-notes-btn.has-notes{background:var(--gold);border-color:var(--gold);color:#09090e !important;}
.tl-notes-btn.has-notes:hover{background:#e8c96a;border-color:#e8c96a;}
.tl-source{font-family:var(--font-mono);font-size:0.54rem;color:var(--muted);padding:0 2rem 2rem;opacity:0.55;}

/* ── STAT PANELS ── */
.tl-stats-bar{display:grid;grid-template-columns:repeat(4,1fr);gap:1.25rem;padding:1.75rem 2.5rem 1.75rem;background:var(--surface);border-bottom:1px solid var(--border);}
.tl-stat-card{background:var(--bg);border:1px solid var(--border);border-radius:12px;padding:1.6rem 1.5rem 1.25rem;display:flex;flex-direction:column;align-items:center;text-align:center;min-height:180px;}
.tl-stat-card.clickable{cursor:pointer;}
.tl-stat-card.clickable:hover{border-color:var(--gold-dim);}
.tl-stat-label{font-family:var(--font-mono);font-size:0.72rem;color:var(--text);letter-spacing:0.08em;text-transform:uppercase;margin-bottom:0.6rem;}
.tl-stat-value{font-family:var(--font-display);font-size:2.8rem;font-weight:800;color:var(--gold);line-height:1;margin-bottom:0.35rem;}
.tl-stat-sub{font-family:var(--font-mono);font-size:0.68rem;color:var(--muted);margin-bottom:0.75rem;}
.tl-stat-btn{display:block;width:100%;margin-top:auto;padding:0.5rem 1rem;background:rgba(201,168,76,0.06);border:1px solid var(--gold-dim);border-radius:8px;font-family:var(--font-mono);font-size:0.68rem;color:var(--gold);cursor:pointer;transition:all 0.18s;letter-spacing:0.05em;text-align:center;}
.tl-stat-btn:hover{background:var(--gold-glow);}
[data-theme="light"] .tl-stats-bar{background:#eef1f7;}
[data-theme="light"] .tl-stat-card{background:#dbe4f5;border-color:rgba(26,39,68,0.15);box-shadow:0 1px 4px rgba(26,39,68,0.08);}
[data-theme="light"] .tl-stat-value{color:#1a2744;}
[data-theme="light"] .tl-stat-label{color:#1a2744;}
[data-theme="light"] .tl-stat-btn{background:rgba(26,39,68,0.06);border-color:rgba(26,39,68,0.2);color:#1a2744;}
[data-theme="light"] .tl-stat-btn:hover{background:rgba(26,39,68,0.1);}

/* ── DRAG TO REORDER ── */
.tl-drag-handle{cursor:grab;color:var(--border);font-size:1rem;padding:0 0.4rem;flex-shrink:0;user-select:none;transition:color 0.15s;}
.tl-drag-handle:hover{color:var(--gold);}
.tl-row.dragging{opacity:0.4;}
.tl-row.drag-over{border-top:2px solid var(--gold);}
.tl-drag-hint{padding:0.55rem 2rem;background:rgba(201,168,76,0.04);border-bottom:1px solid var(--border);font-family:var(--font-mono);font-size:0.6rem;color:var(--muted);letter-spacing:0.06em;display:flex;align-items:center;gap:0.5rem;}
[data-theme="light"] .tl-drag-hint{background:rgba(26,39,68,0.03);color:#6b7280;}
@media(max-width:1200px){
  .mast-btn{padding:0.3rem 0.45rem;font-size:0.58rem;letter-spacing:0.03em;}
  .masthead-nav{gap:0.2rem;}
}
@media(max-width:900px){
  .mast-btn{padding:0.28rem 0.38rem;font-size:0.55rem;}
  .masthead-nav{gap:0.15rem;}
}
@media(max-width:768px){
  .masthead-nav{display:none !important;}
  .legisim-masthead{flex-shrink:0;background:var(--surface);border-bottom:2px solid rgba(201,168,76,0.22);padding:0 1.25rem;display:flex;align-items:center;justify-content:space-between;height:58px;position:relative;z-index:20;}
}
@media(max-width:900px){.tl-grid{grid-template-columns:1fr;padding:1rem;}.tl-header{padding:1.25rem;}}

/* ── LIGHT MODE OVERRIDES ── */
[data-theme="light"] body{background:#eef1f7;color:#1a1e2e;}
[data-theme="light"] .page,[data-theme="light"] .app,[data-theme="light"] .bt-app,[data-theme="light"] .tl-app,[data-theme="light"] .map-app,[data-theme="light"] .cf-app{background:#eef1f7;}
[data-theme="light"] .legisim-masthead{background:#1a2744;border-bottom-color:rgba(255,255,255,0.1);}
[data-theme="light"] .legisim-masthead::before{background:linear-gradient(90deg,transparent,var(--gold),transparent);}
[data-theme="light"] .masthead-wordmark{color:#e6e4dc;}
[data-theme="light"] .masthead-wordmark em{color:#c9a84c;}
[data-theme="light"] .masthead-wordmark sup{color:rgba(230,228,220,0.5);}
[data-theme="light"] .mast-back{border-color:rgba(201,168,76,0.6);color:#c9a84c;background:transparent;}
[data-theme="light"] .mast-back:hover{background:rgba(201,168,76,0.12);border-color:#c9a84c;color:#c9a84c;}
[data-theme="light"] .mast-btn-sm{color:#e6e4dc;border-color:rgba(201,168,76,0.4);}
[data-theme="light"] .mast-btn-sm:hover{color:#ffffff;border-color:#c9a84c;background:rgba(201,168,76,0.1);}
[data-theme="light"] .mast-btn-sm.signout:hover{border-color:rgba(192,57,43,0.7);color:#ff6b6b;background:rgba(192,57,43,0.1);}
[data-theme="light"] .sidebar,[data-theme="light"] .bt-left,[data-theme="light"] .cal-sidebar,[data-theme="light"] .cf-sidebar,[data-theme="light"] .map-left{background:#f8f9fc;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .main,[data-theme="light"] .bt-detail,[data-theme="light"] .cf-main{background:#eef1f7;}
[data-theme="light"] .filter-btn,[data-theme="light"] .vh-filter-btn{background:#f8f9fc;border-color:rgba(26,39,68,0.12);color:#6b7280;}
[data-theme="light"] .filter-btn.active,[data-theme="light"] .vh-filter-btn.active{background:rgba(26,39,68,0.1);border-color:rgba(26,39,68,0.3);color:#1a2744;}
[data-theme="light"] .filter-btn.dem-active{background:var(--dem-bg);border-color:var(--dem);color:var(--dem);}
[data-theme="light"] .filter-btn.rep-active{background:var(--rep-bg);border-color:var(--rep);color:var(--rep);}
[data-theme="light"] .search-input,[data-theme="light"] .chat-input,[data-theme="light"] .bt-search-input,[data-theme="light"] .tl-search-input{background:#f8f9fc;border-color:rgba(26,39,68,0.12);color:#1a1e2e;}
[data-theme="light"] .leg-item.selected{background:#e8ecf4;}
[data-theme="light"] .leg-item.selected::before{background:#1a2744;}
[data-theme="light"] .leg-item:hover{background:#e8ecf4;}
[data-theme="light"] .chat-input-area{background:#f8f9fc;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .msg-bubble{background:#f0f2f8;border-color:rgba(26,39,68,0.1);}
[data-theme="light"] .msg.user .msg-bubble{background:#e8ecf4;border-color:rgba(26,39,68,0.2);}
[data-theme="light"] .send-btn{background:#1a2744;color:#f8f9fc;}
[data-theme="light"] .main-topbar,[data-theme="light"] .news-topbar,[data-theme="light"] .chamber-bar,[data-theme="light"] .toggle-bar{background:#f8f9fc;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .vh-header,[data-theme="light"] .vh-filters,[data-theme="light"] .vh-stats{background:#f8f9fc;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .vh-bill-number,[data-theme="light"] .bt-bill-number,[data-theme="light"] .bt-detail-number,[data-theme="light"] .tl-bill-number,[data-theme="light"] .district-num{color:#1a2744;}
[data-theme="light"] .profile-section-label,[data-theme="light"] .section-label,[data-theme="light"] .form-label,[data-theme="light"] .panel-title,[data-theme="light"] .output-label,[data-theme="light"] .floor-title,[data-theme="light"] .detail-title,[data-theme="light"] .search-section-label{color:#1a2744;}
[data-theme="light"] .input-panel{background:#f8f9fc;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .output-panel,[data-theme="light"] .output-body{background:#eef1f7;}
[data-theme="light"] .tl-add-btn,[data-theme="light"] .tl-refresh-btn,[data-theme="light"] .tl-view-btn{color:#1a2744;border-color:rgba(26,39,68,0.2);}
[data-theme="light"] .tl-notes-btn{color:#ffffff !important;border-color:#3d6cc4 !important;background:#3d6cc4 !important;}
[data-theme="light"] .tl-notes-btn.has-notes{background:#a07830 !important;border-color:#a07830 !important;color:#ffffff !important;}
[data-theme="light"] .tl-table-head{background:#e8ecf4;}
[data-theme="light"] .tl-tr:hover{background:#e8ecf4;}
[data-theme="light"] .date-group-header{color:#1a2744;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .detail-header,[data-theme="light"] .profile-header,[data-theme="light"] .donors-section,[data-theme="light"] .bt-detail-header{background:#f8f9fc;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .acct-card{background:#f8f9fc;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .news-tab.active{color:#1a2744;border-color:#1a2744;}
[data-theme="light"] .story-source{color:#6b7280;}
[data-theme="light"] .profile-sidebar{background:#f8f9fc;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .home-title{color:#1a2744;}
[data-theme="light"] .cf-amount,[data-theme="light"] .cf-total{color:#1a2744;}
[data-theme="light"] .chamber-btn.active{background:rgba(26,39,68,0.1);color:#1a2744;border-color:rgba(26,39,68,0.3);}
[data-theme="light"] .swing-chip{background:rgba(26,39,68,0.1);color:#1a2744;}
[data-theme="light"] .score-cell.pass{background:rgba(37,107,66,0.12);}
[data-theme="light"] .score-cell.fail{background:rgba(176,48,32,0.08);}
[data-theme="light"] .map-tooltip-district{color:#1a2744;}
[data-theme="light"] .watchlist-btn-small,[data-theme="light"] .watch-btn{border-color:rgba(160,120,48,0.4);color:#a07830;}
[data-theme="light"] .vote-history-btn{background:rgba(160,120,48,0.08);border-color:rgba(160,120,48,0.35);color:#a07830;}
[data-theme="light"] .masthead-badge{background:rgba(26,39,68,0.08);border-color:rgba(26,39,68,0.2);}
[data-theme="light"] .masthead-badge-dot{background:#1a2744;}
[data-theme="light"] .mast-btn.gold{border-color:rgba(160,120,48,0.4);color:#a07830;}
[data-theme="light"] .mast-btn.active{background:rgba(26,39,68,0.1);border-color:rgba(26,39,68,0.3);color:#1a2744;}
[data-theme="light"] .issue-tag{background:#e8ecf4;border-color:rgba(26,39,68,0.12);color:#1a1e2e;}
[data-theme="light"] .qp-btn{background:#f0f2f8;border-color:rgba(26,39,68,0.12);color:#6b7280;}
[data-theme="light"] .empty-card{background:#f0f2f8;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .donor-bar-fill{background:linear-gradient(90deg,#1a2744,#2d4a80);}
[data-theme="light"] .cf-leg-item.active,[data-theme="light"] .bt-bill-item.selected{border-left-color:#1a2744;}
[data-theme="light"] .subject-tag{background:#e8ecf4;border-color:rgba(26,39,68,0.12);color:#1a1e2e;}
[data-theme="light"] .plan-badge,[data-theme="light"] .admin{color:#1a2744;}
[data-theme="light"] .login-card{background:#f8f9fc;border-color:rgba(26,39,68,0.12);box-shadow:0 4px 24px rgba(26,39,68,0.1);}
/* ── FOLLOWED COMMITTEES MODAL ── */
.tl-committees-overlay{position:fixed;inset:0;background:rgba(0,0,0,0.75);z-index:300;display:flex;align-items:center;justify-content:center;padding:2rem;}
.tl-committees-modal{background:var(--charcoal,#16161f);border:1px solid var(--border);border-radius:16px;width:100%;max-width:700px;max-height:80vh;display:flex;flex-direction:column;overflow:hidden;}
.tl-committees-modal-head{padding:1.25rem 1.5rem 1rem;border-bottom:1px solid var(--border);display:flex;align-items:center;justify-content:space-between;flex-shrink:0;}
.tl-committees-modal-title{font-family:var(--font-display);font-size:1.15rem;font-weight:800;color:var(--text);}
.tl-committees-modal-count{font-family:var(--font-mono);font-size:0.62rem;color:var(--muted);letter-spacing:0.08em;margin-top:0.15rem;}
.tl-committees-modal-body{flex:1;overflow-y:auto;padding:1rem 1.5rem;scrollbar-width:thin;}
.tl-committees-search{width:100%;background:var(--surface2);border:1px solid var(--border);border-radius:8px;padding:0.55rem 0.85rem;color:var(--text);font-family:var(--font-mono);font-size:0.72rem;outline:none;transition:border-color 0.18s;margin-bottom:0.85rem;box-sizing:border-box;}
.tl-committees-search:focus{border-color:var(--gold-dim);}
.tl-committees-search::placeholder{color:var(--muted);}
.tl-committees-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(220px,1fr));gap:0.55rem;}
.tl-committee-chip{display:flex;align-items:center;gap:0.5rem;padding:0.55rem 0.75rem;background:var(--surface);border:1px solid var(--border);border-radius:8px;transition:border-color 0.15s;}
.tl-committee-chip:hover{border-color:var(--gold-dim);}
.tl-committee-name{font-size:0.8rem;color:var(--text);flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.tl-committee-chamber{font-family:var(--font-mono);font-size:0.52rem;color:var(--muted);flex-shrink:0;padding:0.1rem 0.35rem;border:1px solid var(--border);border-radius:3px;text-transform:uppercase;}
.tl-committee-unfollow{background:none;border:none;color:var(--muted);cursor:pointer;font-size:0.72rem;flex-shrink:0;padding:0.1rem 0.3rem;border-radius:3px;transition:color 0.15s;line-height:1;}
.tl-committee-unfollow:hover{color:#e74c3c;}
.tl-committees-empty{font-family:var(--font-mono);font-size:0.65rem;color:var(--muted);text-align:center;padding:2rem;}
.tl-committees-modal-foot{padding:0.9rem 1.5rem;border-top:1px solid var(--border);display:flex;justify-content:space-between;align-items:center;flex-shrink:0;}
.tl-committees-add{font-family:var(--font-mono);font-size:0.68rem;color:var(--gold);background:rgba(201,168,76,0.06);border:1px solid var(--gold-dim);border-radius:6px;padding:0.4rem 0.85rem;cursor:pointer;transition:all 0.15s;}
.tl-committees-add:hover{background:rgba(201,168,76,0.1);}
[data-theme="light"] .tl-committees-modal{background:#f8f9fc;}
[data-theme="light"] .tl-committee-chip{background:#ffffff;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .tl-committee-name{color:#1a1e2e;}
[data-theme="light"] .tl-committees-search{background:#eef1f7;border-color:rgba(26,39,68,0.15);color:#1a1e2e;}
`;

async function handleSignOut(supabaseClient, onNavigate) {
  await supabaseClient.auth.signOut();
  onNavigate('home');
}

function NotesModal({ bill, userId, onClose }) {
  const [text, setText] = useState(bill.notes || "");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    await saveNotes(userId, bill.bill_id, text);
    setSaving(false);
    setSaved(true);
    setTimeout(() => { setSaved(false); onClose(text); }, 800);
  };

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000,backdropFilter:"blur(2px)"}} onClick={onClose}>
      <div style={{background:"var(--surface)",border:"1px solid var(--border)",borderRadius:"16px",padding:"1.75rem",width:"100%",maxWidth:"520px",margin:"1rem",display:"flex",flexDirection:"column",gap:"1rem",position:"relative"}} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div>
          <div style={{fontFamily:"var(--font-mono)",fontSize:"0.6rem",color:"var(--muted)",letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:"0.25rem"}}>Notes</div>
          <div style={{fontFamily:"var(--font-display)",fontSize:"1.2rem",fontWeight:800,color:"var(--text)",lineHeight:1.2}}>{bill.bill_number}</div>
          <div style={{fontSize:"0.78rem",color:"var(--muted)",marginTop:"0.2rem",lineHeight:1.4}}>{bill.title}</div>
        </div>
        {/* Textarea */}
        <textarea
          autoFocus
          value={text}
          onChange={e => setText(e.target.value)}
          placeholder="Add your notes, talking points, stakeholder concerns, or anything else relevant to this bill..."
          style={{background:"var(--surface2)",border:"1px solid var(--border)",borderRadius:"10px",padding:"0.85rem 1rem",color:"var(--text)",fontFamily:"var(--font-body)",fontSize:"0.85rem",lineHeight:1.6,resize:"vertical",minHeight:"160px",outline:"none",width:"100%"}}
          onFocus={e => e.target.style.borderColor = "var(--gold-dim)"}
          onBlur={e => e.target.style.borderColor = "var(--border)"}
        />
        {/* Footer */}
        <div style={{display:"flex",gap:"0.65rem",justifyContent:"flex-end",alignItems:"center"}}>
          <span style={{fontFamily:"var(--font-mono)",fontSize:"0.6rem",color:"var(--muted)",flex:1}}>{text.length > 0 ? `${text.length} chars` : ""}</span>
          <button onClick={onClose} style={{background:"transparent",border:"1px solid var(--border)",borderRadius:"8px",padding:"0.45rem 1rem",color:"var(--muted)",fontFamily:"var(--font-mono)",fontSize:"0.7rem",cursor:"pointer"}}>Cancel</button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{background:saved?"rgba(37,107,66,0.15)":"var(--gold)",color:saved?"#2e8b57":"#09090e",border:saved?"1px solid rgba(37,107,66,0.4)":"none",borderRadius:"8px",padding:"0.45rem 1.25rem",fontFamily:"var(--font-mono)",fontSize:"0.7rem",fontWeight:600,cursor:"pointer",transition:"all 0.2s",minWidth:"80px"}}
          >
            {saved ? "✓ Saved" : saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function TrackedLegislationPage({ onNavigate, session, profile }) {
  const [bills, setBills] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [updatedIds, setUpdatedIds] = useState(new Set());
  const [loadingList, setLoadingList] = useState(true);
  const { toasts, toast, dismiss } = useToast();
  const [confirmState, setConfirmState] = useState(null);
  const confirm = (message, subtext, onConfirm) => setConfirmState({ message, subtext, onConfirm });
  const [search, setSearch] = useState("");
  const [notesBill, setNotesBill] = useState(null);

  // ── Stat panel state ──
  const [monthlyPAs,   setMonthlyPAs]   = useState([]);
  const [loadingPAs,   setLoadingPAs]   = useState(true);
  const [weeklyBills,  setWeeklyBills]  = useState([]);
  const [loadingWeekly,setLoadingWeekly]= useState(true);
  const [showPAs,      setShowPAs]      = useState(false);
  const [showWeekly,   setShowWeekly]   = useState(false);
  const [showTracked,  setShowTracked]  = useState(false);


  // ── Followed committees ──
  const [followedCommittees,  setFollowedCommittees]  = useState([]);
  const [showCommittees,      setShowCommittees]      = useState(false);
  const [committeeSearch,     setCommitteeSearch]     = useState("");
  const [loadingCommittees,   setLoadingCommittees]   = useState(true);

  // ── Drag state ──
  const dragBillId  = React.useRef(null);
  const dragOverId  = React.useRef(null);

  const userId = session?.user?.id;
  const previousLogin = profile?.previous_last_login ? new Date(profile.previous_last_login) : null;

  // ── Fetch monthly PAs ──
  useEffect(() => {
    const monthStart = new Date(); monthStart.setDate(1); monthStart.setHours(0,0,0,0);
    supabase.from("bills")
      .select("bill_id,bill_number,title,status,status_text,last_action,last_action_date")
      .or("status.eq.3,status_text.eq.Enrolled,status_text.eq.Chaptered")
      .gte("last_action_date", monthStart.toISOString().split("T")[0])
      .order("last_action_date", { ascending: false })
      .then(({ data }) => { setMonthlyPAs(data || []); setLoadingPAs(false); });
  }, []);

  // ── Fetch weekly bills ──
  useEffect(() => {
    fetch(`${PROXY}/api/db/masterlist`)
      .then(r => r.json())
      .then(d => {
        const weekAgo = new Date(Date.now() - 7 * 86400000);
        setWeeklyBills((d.bills || []).filter(b => b.last_action_date && new Date(b.last_action_date) >= weekAgo));
        setLoadingWeekly(false);
      })
      .catch(() => setLoadingWeekly(false));
  }, []);

  // ── Fetch followed committees ──
  useEffect(() => {
    if (!userId) return;
    supabase.from("followed_committees")
      .select("committee_id,committee_name,chamber,type")
      .eq("user_id", userId)
      .order("committee_name", { ascending: true })
      .then(({ data }) => { setFollowedCommittees(data || []); setLoadingCommittees(false); });
  }, [userId]);

  const handleUnfollow = async (committee_id) => {
    await supabase.from("followed_committees").delete().eq("user_id", userId).eq("committee_id", committee_id);
    setFollowedCommittees(prev => prev.filter(c => c.committee_id !== committee_id));
    toast("Unfollowed ✓");
  };

  // Load watchlist from Supabase on mount
  useEffect(() => {
    if (!userId) { setLoadingList(false); return; }
    getWatchlist(userId).then(list => {
      setBills(list);
      setLoadingList(false);
      if (list.length > 0) handleRefresh(list);
    });
  }, [userId]);

  const handleRefresh = async (initialList) => {
    const watchlist = initialList || bills;
    if (!userId || watchlist.length === 0) { setBills([]); return; }

    setRefreshing(true);
    const newUpdatedIds = new Set();

    try {
      const res = await fetch(`${PROXY}/api/db/masterlist`);
      if (!res.ok) throw new Error(`Server error: ${res.status}`);
      const data = await res.json();
      const masterlistArr = data.bills || [];
      const masterlist = {};
      masterlistArr.forEach(b => { masterlist[b.bill_id] = b; });

      const updates = await Promise.all(
        watchlist.map(async (tracked) => {
          const current = masterlist[tracked.bill_id];
          if (!current) return tracked;

          const newHash = current.change_hash;
          const cachedHash = getBillCache(tracked.bill_id)?.change_hash;

          if (newHash === cachedHash) {
            const cached = getBillCache(tracked.bill_id);
            const hasFullDetails = cached?.bill && cached.bill.sponsors !== undefined && cached.bill.history !== undefined;
            if (hasFullDetails) {
              const b = cached.bill;
              return {
                ...tracked,
                description:      b.description || tracked.description || null,
                status:           b.status_text || statusLabel(b.status) || tracked.status,
                last_action:      b.last_action || tracked.last_action,
                last_action_date: b.last_action_date || tracked.last_action_date,
                sponsors:         b.sponsors || [],
                history:          b.history || [],
                change_hash:      newHash,
              };
            }
            try {
              const billRes = await fetch(`${PROXY}/api/db/bill?id=${tracked.bill_id}`);
              if (billRes.ok) {
                const billData = await billRes.json();
                const b = billData.bill;
                if (b) {
                  setBillCache(tracked.bill_id, b, newHash);
                  return {
                    ...tracked,
                    description:      b.description || tracked.description || null,
                    status:           b.status_text || statusLabel(b.status) || tracked.status,
                    last_action:      b.last_action || tracked.last_action,
                    last_action_date: b.last_action_date || tracked.last_action_date,
                    sponsors:         b.sponsors || [],
                    history:          b.history || [],
                    change_hash:      newHash,
                  };
                }
              }
            } catch(e) {}
            return tracked;
          }

          // Hash changed — fetch updated bill detail
          try {
            const billRes = await fetch(`${PROXY}/api/db/bill?id=${tracked.bill_id}`);
            if (!billRes.ok) return tracked;
            const billData = await billRes.json();
            const bill = billData.bill;
            if (!bill) return tracked;

            setBillCache(tracked.bill_id, bill, newHash);
            newUpdatedIds.add(tracked.bill_id);

            const updatedItem = {
              ...tracked,
              title:            bill.title || tracked.title,
              description:      bill.description || tracked.description,
              status:           bill.status_text || statusLabel(bill.status),
              last_action:      bill.last_action || tracked.last_action,
              last_action_date: bill.last_action_date || tracked.last_action_date,
              sponsors:         bill.sponsors || [],
              history:          bill.history || [],
              change_hash:      newHash,
            };

            // Persist updated fields to Supabase
            await updateWatchlistItem(userId, updatedItem);
            return updatedItem;
          } catch { return tracked; }
        })
      );

      setBills(updates);
      setUpdatedIds(newUpdatedIds);
      setLastRefresh(new Date());
    } catch (err) {
      console.error("Refresh error:", err);
    }

    setRefreshing(false);
  };

  const exportPDF = (billList) => {
    const date = new Date().toLocaleDateString("en-US", {month:"long",day:"numeric",year:"numeric"});
    const esc = s => (s||"").replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;");
    const rows = billList.map(b => {
      const nickname = b.nickname ? `<span class="nickname">${esc(b.nickname)}</span>` : "";
      const fullTitle = esc(b.title || "");
      const titleCell = nickname
        ? `${nickname}<br/><span class="full-title">${fullTitle}</span>`
        : fullTitle;
      const lastDate = b.last_action_date ? new Date(b.last_action_date).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"}) : "—";
      const status = esc(b.status || "—");
      const stanceRaw = (b.stance || "").toLowerCase();
      const stanceColor = stanceRaw === "support" ? "#3ba360" : stanceRaw === "oppose" ? "#c0392b" : "inherit";
      const stance = b.stance ? `<span style="color:${stanceColor};font-weight:600">${esc(b.stance)}</span>` : "—";
      const notes = esc(b.notes || "");
      return `<tr>
        <td class="bill-num">${esc(b.bill_number || "—")}</td>
        <td class="title">${titleCell}</td>
        <td class="status">${status}</td>
        <td class="date">${lastDate}</td>
        <td class="stance">${stance}</td>
        <td class="notes">${notes}</td>
      </tr>`;
    }).join("");

    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8"/>
  <title>Tracked Legislation — WhipCount</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,800;1,800&family=IBM+Plex+Mono:wght@400;600;700&display=swap');
    *{box-sizing:border-box;margin:0;padding:0;}
    body{font-family:'IBM Plex Mono',monospace;background:#fff;color:#1a1e2e;padding:40px 48px;}
    .header{margin-bottom:24px;border-bottom:2px solid #1a1e2e;padding-bottom:16px;}
    h1{font-family:'Playfair Display',serif;font-size:28px;font-weight:800;color:#1a1e2e;}
    h1 em{font-style:italic;color:#a07830;}
    .meta{font-size:10px;color:#888;letter-spacing:0.1em;margin-top:6px;text-transform:uppercase;}
    table{width:100%;border-collapse:collapse;font-size:11px;}
    thead tr{background:#1a1e2e;}
    thead th{color:#c9a84c;font-size:9px;font-weight:700;letter-spacing:0.12em;text-transform:uppercase;padding:10px 10px;text-align:left;}
    tbody tr:nth-child(even){background:#f8f9fc;}
    tbody tr{border-bottom:1px solid #e5e7eb;}
    td{padding:8px 10px;vertical-align:top;line-height:1.4;}
    .bill-num{font-weight:700;color:#1a2744;white-space:nowrap;width:70px;}
    .title{color:#374151;width:220px;word-wrap:break-word;}
    .nickname{font-weight:700;color:#1a2744;display:block;}
    .full-title{font-size:9.5px;color:#6b7280;}
    .status{color:#a07830;white-space:nowrap;width:90px;}
    .date{white-space:nowrap;width:90px;}
    .stance{white-space:nowrap;width:70px;}
    .notes{color:#374151;width:160px;word-wrap:break-word;font-size:10px;}
    .footer{margin-top:24px;padding-top:12px;border-top:1px solid #e5e7eb;font-size:9px;color:#aaa;letter-spacing:0.06em;}
    @media print{
      body{padding:24px 32px;}
      @page{margin:0.5in;size:letter landscape;}
    }
  </style>
</head>
<body>
  <div class="header">
    <h1>Tracked <em>Legislation</em></h1>
    <div class="meta">${billList.length} BILL${billList.length!==1?"S":""} &nbsp;·&nbsp; 103RD MICHIGAN LEGISLATURE &nbsp;·&nbsp; Generated ${date}</div>
  </div>
  <table>
    <thead>
      <tr>
        <th>Bill</th>
        <th>Title</th>
        <th>Status</th>
        <th>Last Action</th>
        <th>Position</th>
        <th>Notes</th>
      </tr>
    </thead>
    <tbody>${rows}</tbody>
  </table>
  <div class="footer">WhipCount™ &nbsp;·&nbsp; whipcount.us &nbsp;·&nbsp; Legislative data from LegiScan (CC BY 4.0)</div>
  <script>window.onload = function(){ window.print(); };<\/script>
</body>
</html>`;

    const win = window.open("", "_blank");
    if (win) {
      win.document.write(html);
      win.document.close();
      toast("Opening print dialog…");
    } else {
      toast("Allow popups to export PDF", "warning");
    }
  };

  const handleRemove = (bill_id) => {
    const bill = bills.find(b => b.bill_id === bill_id);
    confirm(
      `Remove ${bill?.bill_number || "this bill"}?`,
      "It will be removed from your tracked legislation.",
      async () => {
        await removeFromWatchlist(userId, bill_id);
        setBills(prev => prev.filter(b => b.bill_id !== bill_id));
        toast(`${bill?.bill_number || "Bill"} removed`, "info");
        setConfirmState(null);
      }
    );
  };

  // ── Save sort order to Supabase ──
  const saveSortOrder = async (ordered) => {
    await Promise.all(ordered.map((b, i) =>
      supabase.from("tracked_bills")
        .update({ sort_order: i })
        .eq("user_id", userId)
        .eq("bill_id", b.bill_id)
    ));
  };

  const handleDragStart = (bill_id) => {
    dragBillId.current = bill_id;
  };

  const handleDragOver = (e, bill_id) => {
    e.preventDefault();
    dragOverId.current = bill_id;
  };

  const handleDrop = (e, targetId) => {
    e.preventDefault();
    const fromId = dragBillId.current;
    if (!fromId || fromId === targetId) return;
    setBills(prev => {
      const next = [...prev];
      const fromIdx = next.findIndex(b => b.bill_id === fromId);
      const toIdx   = next.findIndex(b => b.bill_id === targetId);
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved);
      saveSortOrder(next);
      return next;
    });
    dragBillId.current  = null;
    dragOverId.current  = null;
  };

  const handleDragEnd = () => {
    dragBillId.current  = null;
    dragOverId.current  = null;
    // Remove drag-over class from all rows
    document.querySelectorAll(".tl-row.drag-over").forEach(el => el.classList.remove("drag-over"));
  };

  // Bills updated since last login — persists across refreshes unlike updatedIds
  const sinceLastLogin = previousLogin
    ? new Set(bills.filter(b => b.last_action_date && new Date(b.last_action_date) > previousLogin).map(b => b.bill_id))
    : new Set();

  // Filter bills by search
  const filteredBills = search.trim()
    ? bills.filter(b => {
        const q = search.trim().toLowerCase();
        return (
          (b.bill_number || "").toLowerCase().includes(q) ||
          (b.title || "").toLowerCase().includes(q) ||
          (b.status || "").toLowerCase().includes(q) ||
          (b.last_action || "").toLowerCase().includes(q) ||
          (b.chamber || "").toLowerCase().includes(q) ||
          (b.nickname || "").toLowerCase().includes(q)
        );
      })
    : bills;

  return (
    <div className="tl-app">
      <style>{CSS}</style>
      <MobileMenuCSS />
      <div className="legisim-masthead">
        <div className="masthead-left">
          <div className="masthead-wordmark">Whip<em>Count</em><sup>TM</sup></div>
          <button className="mast-back" onClick={() => onNavigate("dashboard")}>{"← Dashboard"}</button>
        </div>
        <div className="masthead-right">
          <button className="mast-btn-sm" onClick={() => onNavigate("account")}>Account</button>
          <button className="mast-btn-sm signout" onClick={() => handleSignOut(supabase, onNavigate)}>Sign Out</button>
        </div>
      </div>
      <div className="tl-body">
        {/* HEADER */}
        <div style={{padding:"2rem 2.5rem 1.5rem",background:"var(--surface)",borderBottom:"1px solid var(--border)",display:"flex",alignItems:"flex-end",justifyContent:"space-between",gap:"1.5rem",flexWrap:"wrap"}}>
          <div>
            <div style={{fontFamily:"var(--font-display)",fontSize:"2.8rem",fontWeight:900,letterSpacing:"-0.03em",color:"var(--text)",lineHeight:1}}>
              Tracked <em style={{fontStyle:"italic",color:"var(--gold)"}}>Legislation</em>
            </div>
            <div style={{fontFamily:"var(--font-mono)",fontSize:"0.68rem",color:"var(--muted)",letterSpacing:"0.1em",marginTop:"0.6rem"}}>
              {bills.length} BILL{bills.length !== 1 ? "S" : ""} · 103RD MICHIGAN LEGISLATURE
            </div>
          </div>
          <div className="tl-header-right">
            <input
              className="tl-search-input"
              placeholder="Search by bill, title, status…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {refreshing
              ? <div className="tl-refreshing"><div className="tl-spin" /> Checking…</div>
              : <button className="tl-refresh-btn" onClick={handleRefresh}>↺ Refresh</button>
            }
            <button className="tl-add-btn" onClick={() => onNavigate("billtracker")}>+ Add Bills</button>
            <button className="tl-add-btn" style={{background:"var(--gold)",color:"#09090e",border:"none",fontWeight:700}} onClick={() => exportPDF(filteredBills)}>⬇ Export PDF</button>
          </div>
        </div>

        {/* ── STAT PANELS ── */}
        <div className="tl-stats-bar">
          <div className="tl-stat-card clickable" onClick={() => setShowTracked(true)}>
            <div className="tl-stat-label">Your Tracked Bills</div>
            <div className="tl-stat-value">{loadingList ? "—" : bills.length}</div>
            <div className="tl-stat-sub">bills on your watchlist</div>
            <button className="tl-stat-btn" onClick={e => { e.stopPropagation(); onNavigate("billtracker"); }}>+ Add More Bills →</button>
          </div>
          <div className="tl-stat-card clickable" onClick={() => setShowPAs(true)}>
            <div className="tl-stat-label">Public Acts</div>
            <div className="tl-stat-value">{loadingPAs ? "—" : monthlyPAs.length}</div>
            <div className="tl-stat-sub">signed this month</div>
            <button className="tl-stat-btn">View Public Acts →</button>
          </div>
          <div className="tl-stat-card clickable" onClick={() => !loadingWeekly && setShowWeekly(true)}>
            <div className="tl-stat-label">Bills This Week</div>
            <div className="tl-stat-value">{loadingWeekly ? "—" : weeklyBills.length}</div>
            <div className="tl-stat-sub">introduced in last 7 days</div>
            <button className="tl-stat-btn">View This Week's Bills →</button>
          </div>
          <div className="tl-stat-card clickable" onClick={() => setShowCommittees(true)}>
            <div className="tl-stat-label">Followed Committees</div>
            <div className="tl-stat-value">{loadingCommittees ? "—" : followedCommittees.length}</div>
            <div className="tl-stat-sub">committees tracked</div>
            <button className="tl-stat-btn" onClick={e => { e.stopPropagation(); setShowCommittees(true); }}>
              Manage →
            </button>
          </div>
        </div>

        {loadingList ? (
          <table style={{width:"100%",borderCollapse:"collapse"}}><tbody><TableRowSkeleton cols={5} rows={8} /></tbody></table>
        ) : bills.length === 0 ? (
          <div className="tl-empty">
            <div className="tl-empty-icon">{"📌"}</div>
            <div className="tl-empty-title">No bills tracked yet</div>
            <div className="tl-empty-text">
              Browse the Bill Search and click Track This Bill on any bill.
            </div>
            <button className="tl-add-btn" onClick={() => onNavigate("billtracker")}>Browse Bills</button>
          </div>
        ) : filteredBills.length === 0 ? (
          <div className="tl-empty">
            <div className="tl-empty-icon">{"🔍"}</div>
            <div className="tl-empty-title">No results</div>
            <div className="tl-empty-text">No tracked bills match your search.</div>
          </div>
        ) : (
          <React.Fragment>
            <div className="tl-drag-hint">
              ⠿ &nbsp;Drag the handle on any row to reorder your tracked bills — your order is saved automatically.
            </div>
            <div className="tl-table-wrap">
              <table className="tl-table">
                <thead className="tl-table-head">
                  <tr>
                    <th className="tl-th" style={{width:"28px",padding:"0.75rem 0.5rem"}}></th>
                    <th className="tl-th">Bill</th>
                    <th className="tl-th">Status</th>
                    <th className="tl-th">Last Action</th>
                    <th className="tl-th" style={{textAlign:"right"}}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBills.map(bill => {
                    const sc = statusStyle(bill.status);
                    const wasUpdated = updatedIds.has(bill.bill_id) || sinceLastLogin.has(bill.bill_id);
                    return (
                      <tr
                        key={bill.bill_id}
                        className={"tl-row" + (wasUpdated ? " updated" : "")}
                        draggable
                        onDragStart={() => handleDragStart(bill.bill_id)}
                        onDragOver={e => { handleDragOver(e, bill.bill_id); e.currentTarget.classList.add("drag-over"); }}
                        onDragLeave={e => e.currentTarget.classList.remove("drag-over")}
                        onDrop={e => { e.currentTarget.classList.remove("drag-over"); handleDrop(e, bill.bill_id); }}
                        onDragEnd={handleDragEnd}
                      >
                        <td className="tl-td" style={{padding:"0.75rem 0.25rem 0.75rem 0.75rem",width:"28px"}}>
                          <span className="tl-drag-handle" title="Drag to reorder">⠿</span>
                        </td>
                        <td className="tl-td">
                          <div style={{display:"flex",alignItems:"baseline",gap:"0.5rem",flexWrap:"wrap"}}>
                            <div className="tl-bill-number">{bill.bill_number}</div>
                            <div className="tl-nickname-wrap">
                              <span className="tl-nickname-label">—</span>
                              <input
                                className="tl-nickname-input"
                                placeholder="add nickname"
                                defaultValue={bill.nickname || ""}
                                onBlur={async e => {
                                  const val = e.target.value.trim();
                                  if (val !== (bill.nickname || "").trim()) {
                                    setBills(prev => prev.map(b => b.bill_id === bill.bill_id ? { ...b, nickname: val } : b));
                                    await saveNickname(userId, bill.bill_id, val);
                                  }
                                }}
                                onKeyDown={e => { if (e.key === "Enter") e.target.blur(); }}
                                onClick={e => e.stopPropagation()}
                              />
                            </div>
                          </div>
                          <div className="tl-bill-title">{bill.title}</div>
                          {bill.chamber && <div className="tl-bill-chamber">{bill.chamber}</div>}
                        </td>
                        <td className="tl-td">
                          <span className="tl-status-badge" style={{ background:sc.bg, border:"1px solid "+sc.border, color:sc.text }}>
                            {displayStatus(bill.status)}
                          </span>
                          {wasUpdated && <span className="tl-updated-badge">Updated</span>}
                        </td>
                        <td className="tl-td">
                          <div className="tl-action-text">{bill.last_action || "—"}</div>
                          {bill.last_action_date && (
                            <div className="tl-date-text">
                              {new Date(bill.last_action_date).toLocaleDateString("en-US", { month:"long", day:"numeric", year:"numeric" })}
                            </div>
                          )}
                        </td>
                        <td className="tl-td">
                          <div className="tl-row-actions">
                            <button className="tl-view-btn" onClick={() => { sessionStorage.setItem("wc_open_bill_id", String(bill.bill_id)); onNavigate("billtracker"); }}>View in Search</button>
                            <button
                              className={`tl-notes-btn${bill.notes ? " has-notes" : ""}`}
                              onClick={() => setNotesBill(bill)}
                              title={bill.notes || "Add notes"}
                            >{bill.notes ? "📝 Notes" : "+ Notes"}</button>
                            <select
                              className={`tl-stance-btn${bill.stance ? ` ${bill.stance.toLowerCase()}` : ""}`}
                              value={bill.stance || ""}
                              onChange={async (e) => {
                                const val = e.target.value || null;
                                await saveStance(userId, bill.bill_id, val);
                                setBills(prev => prev.map(b => b.bill_id === bill.bill_id ? { ...b, stance: val } : b));
                              }}
                            >
                              <option value="">Stance</option>
                              <option value="support">Support</option>
                              <option value="neutral">Neutral</option>
                              <option value="oppose">Oppose</option>
                            </select>
                            <button className="tl-remove-btn" onClick={() => handleRemove(bill.bill_id)}>Remove</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="tl-source">
              Data provided by LegiScan (legiscan.com) · CC BY 4.0
            </div>
          </React.Fragment>
        )}
      </div>
    {/* Tracked Bills Modal */}
    {showTracked && (
      <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",zIndex:200,display:"flex",alignItems:"center",justifyContent:"center",padding:"2rem"}} onClick={() => setShowTracked(false)}>
        <div style={{background:"var(--charcoal,#16161f)",border:"1px solid var(--border)",borderRadius:"16px",padding:0,maxWidth:"620px",width:"100%",maxHeight:"80vh",display:"flex",flexDirection:"column",overflow:"hidden"}} onClick={e => e.stopPropagation()}>
          <div style={{padding:"1.5rem 2rem 1rem",borderBottom:"1px solid var(--border)"}}>
            <div style={{fontFamily:"var(--font-display)",fontSize:"1.25rem",fontWeight:800,color:"var(--text)",marginBottom:"0.25rem"}}>Your Tracked Bills</div>
            <div style={{fontFamily:"var(--font-mono)",fontSize:"0.62rem",color:"var(--muted)"}}>{bills.length} bill{bills.length !== 1 ? "s" : ""} on your watchlist · 103rd Michigan Legislature</div>
          </div>
          <div style={{flex:1,overflowY:"auto",padding:"0.5rem 2rem",scrollbarWidth:"thin"}}>
            {bills.length === 0
              ? <div style={{padding:"2rem",textAlign:"center",fontFamily:"var(--font-mono)",fontSize:"0.7rem",color:"var(--muted)"}}>No bills tracked yet. Browse Bill Search to add bills.</div>
              : bills.map(b => {
                  const sc = statusStyle(b.status);
                  return (
                    <div key={b.bill_id} style={{padding:"0.75rem 0",borderBottom:"1px solid rgba(255,255,255,0.04)",display:"flex",alignItems:"flex-start",gap:"0.75rem"}}>
                      <div style={{flex:1}}>
                        <div style={{display:"flex",alignItems:"baseline",gap:"0.5rem",marginBottom:"0.15rem"}}>
                          <div style={{fontFamily:"var(--font-mono)",fontSize:"0.72rem",color:"var(--gold)",fontWeight:600}}>{b.bill_number}</div>
                          {b.nickname && <div style={{fontFamily:"var(--font-mono)",fontSize:"0.62rem",color:"var(--muted)",fontStyle:"italic"}}>"{b.nickname}"</div>}
                        </div>
                        <div style={{fontSize:"0.85rem",color:"var(--text)",lineHeight:1.4,marginBottom:"0.2rem"}}>{b.title}</div>
                        <div style={{fontFamily:"var(--font-mono)",fontSize:"0.58rem",color:"var(--muted)"}}>{b.last_action_date ? new Date(b.last_action_date).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"}) : ""}</div>
                      </div>
                      <span style={{flexShrink:0,marginTop:"0.15rem",fontFamily:"var(--font-mono)",fontSize:"0.62rem",padding:"0.2rem 0.55rem",borderRadius:"5px",background:sc.bg,border:"1px solid "+sc.border,color:sc.text,whiteSpace:"nowrap"}}>
                        {displayStatus(b.status)}
                      </span>
                    </div>
                  );
                })
            }
          </div>
          <div style={{padding:"1rem 2rem",borderTop:"1px solid var(--border)",display:"flex",justifyContent:"flex-end"}}>
            <button onClick={() => setShowTracked(false)} style={{background:"transparent",border:"1px solid var(--border)",borderRadius:"8px",padding:"0.5rem 1.25rem",fontFamily:"var(--font-mono)",fontSize:"0.68rem",color:"var(--muted)",cursor:"pointer"}}>Close</button>
          </div>
        </div>
      </div>
    )}

    {/* PA Modal */}
    {showPAs && (
      <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",zIndex:200,display:"flex",alignItems:"center",justifyContent:"center",padding:"2rem"}} onClick={() => setShowPAs(false)}>
        <div style={{background:"var(--charcoal,#16161f)",border:"1px solid var(--border)",borderRadius:"16px",padding:0,maxWidth:"620px",width:"100%",maxHeight:"80vh",display:"flex",flexDirection:"column",overflow:"hidden"}} onClick={e => e.stopPropagation()}>
          <div style={{padding:"1.5rem 2rem 1rem",borderBottom:"1px solid var(--border)"}}>
            <div style={{fontFamily:"var(--font-display)",fontSize:"1.25rem",fontWeight:800,color:"var(--text)",marginBottom:"0.25rem"}}>Public Acts This Month</div>
            <div style={{fontFamily:"var(--font-mono)",fontSize:"0.62rem",color:"var(--muted)"}}>{monthlyPAs.length} bill{monthlyPAs.length !== 1 ? "s" : ""} signed into law this month</div>
          </div>
          <div style={{flex:1,overflowY:"auto",padding:"0.5rem 2rem",scrollbarWidth:"thin"}}>
            {monthlyPAs.length === 0
              ? <div style={{padding:"2rem",textAlign:"center",fontFamily:"var(--font-mono)",fontSize:"0.7rem",color:"var(--muted)"}}>No public acts signed this month yet.</div>
              : monthlyPAs.map(b => (
                <div key={b.bill_id} style={{padding:"0.75rem 0",borderBottom:"1px solid rgba(255,255,255,0.04)"}}>
                  <div style={{fontFamily:"var(--font-mono)",fontSize:"0.62rem",color:"var(--gold)",marginBottom:"0.15rem"}}>{b.bill_number}</div>
                  <div style={{fontSize:"0.85rem",color:"var(--text)",lineHeight:1.4,marginBottom:"0.15rem"}}>{b.title}</div>
                  <div style={{fontFamily:"var(--font-mono)",fontSize:"0.58rem",color:"var(--muted)"}}>PA Signed{b.last_action_date ? ` · ${new Date(b.last_action_date).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})}` : ""}</div>
                </div>
              ))}
          </div>
          <div style={{padding:"1rem 2rem",borderTop:"1px solid var(--border)",display:"flex",justifyContent:"flex-end"}}>
            <button onClick={() => setShowPAs(false)} style={{background:"transparent",border:"1px solid var(--border)",borderRadius:"8px",padding:"0.5rem 1.25rem",fontFamily:"var(--font-mono)",fontSize:"0.68rem",color:"var(--muted)",cursor:"pointer"}}>Close</button>
          </div>
        </div>
      </div>
    )}

    {/* Weekly Bills Modal */}
    {showWeekly && (
      <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",zIndex:200,display:"flex",alignItems:"center",justifyContent:"center",padding:"2rem"}} onClick={() => setShowWeekly(false)}>
        <div style={{background:"var(--charcoal,#16161f)",border:"1px solid var(--border)",borderRadius:"16px",padding:0,maxWidth:"620px",width:"100%",maxHeight:"80vh",display:"flex",flexDirection:"column",overflow:"hidden"}} onClick={e => e.stopPropagation()}>
          <div style={{padding:"1.5rem 2rem 1rem",borderBottom:"1px solid var(--border)"}}>
            <div style={{fontFamily:"var(--font-display)",fontSize:"1.25rem",fontWeight:800,color:"var(--text)",marginBottom:"0.25rem"}}>Bills Introduced This Week</div>
            <div style={{fontFamily:"var(--font-mono)",fontSize:"0.62rem",color:"var(--muted)"}}>{weeklyBills.length} bill{weeklyBills.length !== 1 ? "s" : ""} in the last 7 days</div>
          </div>
          <div style={{flex:1,overflowY:"auto",padding:"0.5rem 2rem",scrollbarWidth:"thin"}}>
            {weeklyBills.map(b => {
              const isTracked = bills.some(t => t.bill_id === b.bill_id);
              return (
                <div key={b.bill_id} style={{padding:"0.75rem 0",borderBottom:"1px solid rgba(255,255,255,0.04)",display:"flex",alignItems:"flex-start",gap:"0.75rem"}}>
                  <div style={{flex:1}}>
                    <div style={{fontFamily:"var(--font-mono)",fontSize:"0.62rem",color:"var(--gold)",marginBottom:"0.15rem"}}>{b.bill_number}</div>
                    <div style={{fontSize:"0.85rem",color:"var(--text)",lineHeight:1.4,marginBottom:"0.15rem"}}>{b.title}</div>
                    <div style={{fontFamily:"var(--font-mono)",fontSize:"0.58rem",color:"var(--muted)"}}>{b.last_action_date ? new Date(b.last_action_date).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"}) : ""}</div>
                  </div>
                  <button
                    onClick={async () => {
                      if (isTracked || !userId) return;
                      const { data } = await supabase.from("tracked_bills").upsert({
                        user_id: userId, bill_id: b.bill_id, bill_number: b.bill_number,
                        title: b.title, status: b.status, last_action: b.last_action,
                        last_action_date: b.last_action_date, change_hash: b.change_hash,
                        tracked_at: new Date().toISOString(), last_seen_at: new Date().toISOString(),
                      }, { onConflict: "user_id,bill_id" }).select().single();
                      if (data) { setBills(prev => [data, ...prev]); toast(`${b.bill_number} tracked ✓`); }
                    }}
                    style={{flexShrink:0,marginTop:"0.15rem",background:isTracked?"rgba(46,125,82,0.12)":"var(--gold-glow)",border:isTracked?"1px solid rgba(46,125,82,0.3)":"1px solid var(--gold-dim)",borderRadius:"6px",padding:"0.28rem 0.65rem",fontFamily:"var(--font-mono)",fontSize:"0.6rem",color:isTracked?"#4ade80":"var(--gold)",cursor:isTracked?"default":"pointer",whiteSpace:"nowrap"}}
                  >{isTracked ? "✓ Tracked" : "+ Track"}</button>
                </div>
              );
            })}
          </div>
          <div style={{padding:"1rem 2rem",borderTop:"1px solid var(--border)",display:"flex",justifyContent:"flex-end"}}>
            <button onClick={() => setShowWeekly(false)} style={{background:"transparent",border:"1px solid var(--border)",borderRadius:"8px",padding:"0.5rem 1.25rem",fontFamily:"var(--font-mono)",fontSize:"0.68rem",color:"var(--muted)",cursor:"pointer"}}>Close</button>
          </div>
        </div>
      </div>
    )}

    {notesBill && (
      <NotesModal
        bill={notesBill}
        userId={userId}
        onClose={(savedText) => {
          if (typeof savedText === "string") {
            setBills(prev => prev.map(b => b.bill_id === notesBill.bill_id ? { ...b, notes: savedText } : b));
          }
          setNotesBill(null);
        }}
      />
    )}
      {confirmState && <ConfirmDialog message={confirmState.message} subtext={confirmState.subtext} onConfirm={confirmState.onConfirm} onCancel={() => setConfirmState(null)} />}
      {/* ── FOLLOWED COMMITTEES MODAL ── */}
      {showCommittees && (
        <div className="tl-committees-overlay" onClick={() => { setShowCommittees(false); setCommitteeSearch(""); }}>
          <div className="tl-committees-modal" onClick={e => e.stopPropagation()}>
            <div className="tl-committees-modal-head">
              <div>
                <div className="tl-committees-modal-title">Followed Committees</div>
                <div className="tl-committees-modal-count">{followedCommittees.length} committee{followedCommittees.length !== 1 ? "s" : ""} tracked</div>
              </div>
              <button onClick={() => { setShowCommittees(false); setCommitteeSearch(""); }} style={{background:"none",border:"none",color:"var(--muted)",cursor:"pointer",fontSize:"1.1rem",lineHeight:1,padding:"0.25rem"}}>✕</button>
            </div>
            <div className="tl-committees-modal-body">
              <input
                className="tl-committees-search"
                placeholder="Search committees…"
                value={committeeSearch}
                onChange={e => setCommitteeSearch(e.target.value)}
                autoFocus
              />
              {loadingCommittees ? (
                <div className="tl-committees-empty">Loading…</div>
              ) : followedCommittees.length === 0 ? (
                <div className="tl-committees-empty">You're not following any committees yet.</div>
              ) : (() => {
                const filtered = followedCommittees.filter(c =>
                  !committeeSearch.trim() ||
                  c.committee_name.toLowerCase().includes(committeeSearch.toLowerCase()) ||
                  (c.chamber||"").toLowerCase().includes(committeeSearch.toLowerCase())
                );
                return filtered.length === 0
                  ? <div className="tl-committees-empty">No committees match your search.</div>
                  : (
                    <div className="tl-committees-grid">
                      {filtered.map(c => (
                        <div key={c.committee_id} className="tl-committee-chip">
                          <span className="tl-committee-name" title={c.committee_name}>{c.committee_name}</span>
                          <span className="tl-committee-chamber">{c.chamber}</span>
                          <button className="tl-committee-unfollow" title="Unfollow" onClick={() => handleUnfollow(c.committee_id)}>✕</button>
                        </div>
                      ))}
                    </div>
                  );
              })()}
            </div>
            <div className="tl-committees-modal-foot">
              <button className="tl-committees-add" onClick={() => { setShowCommittees(false); onNavigate("dashboard"); }}>
                + Follow More Committees
              </button>
              <button onClick={() => { setShowCommittees(false); setCommitteeSearch(""); }} style={{background:"transparent",border:"1px solid var(--border)",borderRadius:"8px",padding:"0.4rem 1.1rem",fontFamily:"var(--font-mono)",fontSize:"0.68rem",color:"var(--muted)",cursor:"pointer"}}>
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer toasts={toasts} dismiss={dismiss} />
    </div>
  );
}
